// assets/cat-name-manager.js
// Centralized Cat Name Manager for Umino Theme - Fixed Version

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    storageKey: 'catName',
    propertyName: 'Cat Name',
    selectors: {
      container: '.cat-name-container',
      display: '.cat-name-display',
      input: '.cat-name-input',
      changeBtn: '.cat-name-change-btn',
      saveBtn: '.cat-name-save-btn',
      cancelBtn: '.cat-name-cancel-btn',
      viewMode: '.cat-name-view-mode',
      editMode: '.cat-name-edit-mode',
      errorMsg: '.cat-name-error',
      required: '.cat-name-required',
      propertyInput: '.cat-name-property-input'
    },
    messages: {
      required: 'Please enter your cat\'s name to continue.',
      checkoutRequired: 'Please enter your cat\'s name before checkout.',
      changed: 'Cat name updated successfully.'
    }
  };

  class CatNameManager {
    constructor() {
      this.name = this.get();
      this.initialized = false;
      this.listeners = [];
      this.cartUpdateInProgress = false;
      this.containers = [];
    }

    // Core API Methods
    get() {
      return sessionStorage.getItem(CONFIG.storageKey) || '';
    }

    set(name) {
      if (typeof name !== 'string') return false;
      const trimmed = name.trim();
      if (trimmed === '') {
        this.clear();
        return false;
      }
      sessionStorage.setItem(CONFIG.storageKey, trimmed);
      this.name = trimmed;
      this.notifyListeners('set', trimmed);
      this.updateCartProperties();
      this.refreshUI();
      return true;
    }

    has() {
      const name = this.get();
      return name !== null && name !== '';
    }

    clear() {
      sessionStorage.removeItem(CONFIG.storageKey);
      this.name = '';
      this.notifyListeners('clear');
      this.refreshUI();
    }

    change(newName) {
      return this.set(newName);
    }

    // Cart Integration
    async updateCartProperties() {
      if (this.cartUpdateInProgress) return;
      
      const name = this.get();
      if (!name) return;

      this.cartUpdateInProgress = true;

      try {
        const cartResponse = await fetch('/cart.js');
        const cart = await cartResponse.json();

        if (!cart.items || cart.items.length === 0) {
          this.cartUpdateInProgress = false;
          return;
        }

        const updates = {};
        let needsUpdate = false;

        cart.items.forEach(item => {
          const currentCatName = item.properties && item.properties[CONFIG.propertyName];
          if (currentCatName !== name) {
            updates[item.key] = {
              id: item.variant_id,
              quantity: item.quantity,
              properties: {
                ...item.properties,
                [CONFIG.propertyName]: name
              }
            };
            needsUpdate = true;
          }
        });

        if (needsUpdate) {
          await this.updateCartItems(updates);
        }

        this.cartUpdateInProgress = false;
      } catch (error) {
        console.error('Failed to update cart properties:', error);
        this.cartUpdateInProgress = false;
      }
    }

    async updateCartItems(updates) {
      const updatePromises = Object.entries(updates).map(([key, data]) => {
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            updates: {
              [key]: data.quantity
            },
            properties: {
              [CONFIG.propertyName]: data.properties[CONFIG.propertyName]
            }
          })
        });
      });

      await Promise.all(updatePromises);
      document.dispatchEvent(new CustomEvent('cart:updated'));
    }

    // Add to Cart Interceptor
    interceptAddToCart(form) {
      const name = this.get();
      
      if (!name) {
        this.showError(CONFIG.messages.required);
        return false;
      }

      let propertyInput = form.querySelector(`input[name="properties[${CONFIG.propertyName}]"]`);
      if (propertyInput) {
        propertyInput.value = name;
      } else {
        propertyInput = document.createElement('input');
        propertyInput.type = 'hidden';
        propertyInput.name = `properties[${CONFIG.propertyName}]`;
        propertyInput.value = name;
        form.appendChild(propertyInput);
      }

      return true;
    }

    // UI Methods
    refreshUI() {
      this.containers = document.querySelectorAll(CONFIG.selectors.container);
      
      // Remove duplicates by ID
      const seenIds = new Set();
      this.containers.forEach(container => {
        const id = container.dataset.id || container.id;
        if (seenIds.has(id)) {
          container.style.display = 'none';
          return;
        }
        if (id) seenIds.add(id);
        this.renderUI(container);
      });
      
      this.updateCartUI();
      this.updateCheckoutButtons();
      
      // Update property inputs
      document.querySelectorAll(CONFIG.selectors.propertyInput).forEach(input => {
        input.value = this.get();
      });
    }

    renderUI(container) {
      if (!container || container.style.display === 'none') return;

      const name = this.get();
      const display = container.querySelector(CONFIG.selectors.display);
      const input = container.querySelector(CONFIG.selectors.input);
      const changeBtn = container.querySelector(CONFIG.selectors.changeBtn);
      const saveBtn = container.querySelector(CONFIG.selectors.saveBtn);
      const cancelBtn = container.querySelector(CONFIG.selectors.cancelBtn);
      const viewMode = container.querySelector(CONFIG.selectors.viewMode);
      const editMode = container.querySelector(CONFIG.selectors.editMode);
      const errorMsg = container.querySelector(CONFIG.selectors.errorMsg);

      if (!display || !input) return;

      if (name) {
        // Show existing name
        display.textContent = name;
        if (viewMode) viewMode.style.display = 'flex';
        if (editMode) editMode.style.display = 'none';
        if (errorMsg) errorMsg.classList.remove('show');
        if (changeBtn) changeBtn.style.display = 'inline-block';
        container.dataset.hasName = 'true';
      } else {
        // Show input
        input.value = '';
        if (viewMode) viewMode.style.display = 'none';
        if (editMode) editMode.style.display = 'block';
        if (errorMsg) errorMsg.classList.remove('show');
        if (changeBtn) changeBtn.style.display = 'none';
        container.dataset.hasName = 'false';
        
        // Focus input if it's visible
        if (container.closest('.active, .open, .bls__quickview-content, #product-form')) {
          setTimeout(() => {
            if (input.offsetParent !== null) {
              input.focus();
            }
          }, 300);
        }
      }
    }

    updateCartUI() {
      const cartContainers = document.querySelectorAll('.cart-drawer .cat-name-container, .cart-page .cat-name-container');
      cartContainers.forEach(container => {
        this.renderUI(container);
      });
      this.updateCheckoutButtons();
    }

    updateCheckoutButtons() {
      const name = this.get();
      const checkoutButtons = document.querySelectorAll(
        '.cart-drawer [name="checkout"], .cart-page [name="checkout"], .cart-drawer .checkout-btn, .cart-page .checkout-btn'
      );

      checkoutButtons.forEach(btn => {
        if (!name) {
          btn.disabled = true;
          btn.dataset.originalText = btn.textContent;
          btn.textContent = 'Enter Cat Name to Checkout';
          btn.classList.add('disabled');
        } else {
          btn.disabled = false;
          if (btn.dataset.originalText) {
            btn.textContent = btn.dataset.originalText;
          }
          btn.classList.remove('disabled');
        }
      });
    }

    showError(message) {
      // Find the active container
      let targetContainer = null;
      this.containers.forEach(container => {
        if (container.closest('.active, .open, .bls__quickview-content, #product-form, .cart-drawer, .cart-page')) {
          targetContainer = container;
        }
      });

      if (targetContainer) {
        const errorMsg = targetContainer.querySelector(CONFIG.selectors.errorMsg);
        if (errorMsg) {
          errorMsg.textContent = message;
          errorMsg.classList.add('show');
          setTimeout(() => {
            errorMsg.classList.remove('show');
          }, 4000);
        }
      } else {
        // Fallback alert
        alert(message);
      }
    }

    // Event Listeners
    setupEventListeners() {
      // Setup all containers
      this.setupAllContainers();

      // DOM ready
      document.addEventListener('DOMContentLoaded', () => {
        this.refreshUI();
        this.setupAllContainers();
      });

      // Cart updates
      document.addEventListener('cart:updated', () => {
        this.refreshUI();
      });

      // Quick view opened
      document.addEventListener('quickview:opened', (event) => {
        setTimeout(() => {
          this.refreshUI();
          const container = document.querySelector('.bls__quickview-content .cat-name-container');
          if (container && !this.get()) {
            const input = container.querySelector(CONFIG.selectors.input);
            if (input) setTimeout(() => input.focus(), 300);
          }
        }, 200);
      });

      // MutationObserver for dynamic content
      this.setupMutationObserver();
    }

    setupAllContainers() {
      document.querySelectorAll(CONFIG.selectors.container).forEach(container => {
        if (!container.dataset.initialized) {
          this.setupContainerListeners(container);
          container.dataset.initialized = 'true';
          this.renderUI(container);
        }
      });
    }

    setupContainerListeners(container) {
      const input = container.querySelector(CONFIG.selectors.input);
      const saveBtn = container.querySelector(CONFIG.selectors.saveBtn);
      const cancelBtn = container.querySelector(CONFIG.selectors.cancelBtn);
      const changeBtn = container.querySelector(CONFIG.selectors.changeBtn);

      // Save button
      if (saveBtn) {
        saveBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const value = input ? input.value.trim() : '';
          this.handleSave(value, container);
        });
      }

      // Enter key on input
      if (input) {
        input.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const value = input.value.trim();
            this.handleSave(value, container);
          }
        });
      }

      // Change button
      if (changeBtn) {
        changeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const viewMode = container.querySelector(CONFIG.selectors.viewMode);
          const editMode = container.querySelector(CONFIG.selectors.editMode);
          const inputEl = container.querySelector(CONFIG.selectors.input);
          
          if (viewMode) viewMode.style.display = 'none';
          if (editMode) editMode.style.display = 'block';
          if (inputEl) {
            inputEl.value = this.get();
            setTimeout(() => inputEl.focus(), 100);
          }
        });
      }

      // Cancel button
      if (cancelBtn) {
        cancelBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.refreshUI();
        });
      }
    }

    handleSave(value, container) {
      if (!value) {
        this.showError(CONFIG.messages.required);
        const input = container.querySelector(CONFIG.selectors.input);
        if (input) {
          input.focus();
          input.classList.add('error');
          setTimeout(() => input.classList.remove('error'), 3000);
        }
        return;
      }

      const success = this.set(value);
      if (success) {
        this.refreshUI();
        document.dispatchEvent(new CustomEvent('catname:changed', {
          detail: { name: value }
        }));
      }
    }

    setupMutationObserver() {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              // Check for new containers
              const containers = node.querySelectorAll 
                ? node.querySelectorAll(CONFIG.selectors.container) 
                : [];
              containers.forEach(container => {
                if (!container.dataset.initialized) {
                  this.setupContainerListeners(container);
                  this.renderUI(container);
                  container.dataset.initialized = 'true';
                }
              });

              // Check for new forms
              const forms = node.querySelectorAll 
                ? node.querySelectorAll('form[action*="/cart/add"]') 
                : [];
              forms.forEach(form => {
                if (!form.dataset.catIntercepted) {
                  this.interceptForm(form);
                  form.dataset.catIntercepted = 'true';
                }
              });
            }
          });
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    interceptForm(form) {
      form.addEventListener('submit', (e) => {
        const name = this.get();
        if (!name) {
          e.preventDefault();
          e.stopPropagation();
          this.showError(CONFIG.messages.required);
          return false;
        }

        let propertyInput = form.querySelector(`input[name="properties[${CONFIG.propertyName}]"]`);
        if (!propertyInput) {
          propertyInput = document.createElement('input');
          propertyInput.type = 'hidden';
          propertyInput.name = `properties[${CONFIG.propertyName}]`;
          propertyInput.value = name;
          form.appendChild(propertyInput);
        } else {
          propertyInput.value = name;
        }

        return true;
      }, true);
    }

    // Notification System
    on(event, callback) {
      this.listeners.push({ event, callback });
    }

    notifyListeners(event, data) {
      this.listeners.forEach(listener => {
        if (listener.event === event) {
          listener.callback(data);
        }
      });
    }

    // Initialize
    init() {
      if (this.initialized) return;
      this.initialized = true;
      
      this.setupEventListeners();
      
      document.querySelectorAll('form[action*="/cart/add"]').forEach(form => {
        if (!form.dataset.catIntercepted) {
          this.interceptForm(form);
          form.dataset.catIntercepted = 'true';
        }
      });

      this.refreshUI();

      if (this.has()) {
        this.updateCartProperties();
      }

      console.log('[CatNameManager] Initialized successfully');
    }
  }

  // Singleton instance
  window.CatNameManager = new CatNameManager();

  // Auto-initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      window.CatNameManager.init();
    });
  } else {
    window.CatNameManager.init();
  }

})();